<template>
  <div
    class="relative flex h-screen w-full max-w-full overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- Sidebar -->
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
      @update:is-open-mobile="isSidebarOpenMobile = $event"
    />


    <!-- Main content -->
    <div class="flex flex-col flex-1 min-w-0 min-h-0 w-full overflow-y-auto custom-scrollbar">
      <!-- Top bar -->
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 min-w-0 px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div class="max-w-6xl mx-auto space-y-4">
          <!-- Page header -->
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div>
              <h1 class="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">
                {{ t("consult_all_title") }}
              </h1>
              <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                {{ t("consult_all_subtitle") }}
              </p>
            </div>
          </div>

          <!-- Card wrapper -->
          <section
            class="overflow-hidden bg-white border shadow-sm dark:bg-slate-900 border-slate-200/80 dark:border-slate-800 rounded-2xl"
          >
            <header
              class="px-3.5 py-3 sm:px-5 border-b border-slate-200/60 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/40 backdrop-blur-sm"
            >
              <div class="flex flex-col sm:flex-row sm:items-center gap-3 min-w-0">
                <FilterSearchInput
                  v-model="searchInput"
                  :lang="lang"
                  :placeholder="t('consult_all_search_placeholder')"
                  :aria-label="t('consult_all_search_placeholder')"
                  :clear-title="lang === 'ar' ? 'مسح' : 'Clear'"
                  @clear="clearSearch"
                  class="w-full sm:w-64 lg:w-72 shrink-0"
                />

                <SearchableSelect
                  v-model="filters.type"
                  :options="typeSelectOptions"
                  :lang="lang"
                  min-width="10rem"
                  max-width="16rem"
                  menu-min-width="18rem"
                  menu-max-width="26rem"
                  :placeholder="t('consult_filter_type_all')"
                  :search-placeholder="t('consult_filter_type_search')"
                  :empty-label="t('consult_filter_type_empty')"
                />
              </div>

              <div
                v-if="hasActiveFilters"
                class="mt-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50"
              >
                <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div
                    class="flex shrink-0 items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400"
                  >
                    <span
                      class="flex items-center justify-center w-6 h-6 rounded-lg bg-primary/10 text-primary"
                    >
                      <i class="mdi mdi-filter-variant text-[14px]"></i>
                    </span>
                    <span class="font-medium text-slate-600 dark:text-slate-300">
                      {{ t("consult_filter_active_hint") }}
                    </span>
                    <span
                      v-if="!isLoading && pagination.total != null"
                      class="tabular-nums font-semibold text-primary"
                    >
                      ({{ pagination.total }})
                    </span>
                  </div>

                  <div class="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                    <button
                      v-for="chip in activeFilterChips"
                      :key="chip.id"
                      type="button"
                      class="group inline-flex items-center gap-1 h-8 max-w-full pl-2.5 pr-1 text-[11px] font-medium rounded-lg border transition-colors border-slate-200/90 bg-white dark:bg-slate-800/80 dark:border-slate-700/80 text-slate-700 dark:text-slate-200 hover:border-primary/35 hover:bg-primary/5 dark:hover:bg-primary/10"
                      :title="t('consult_filter_clear')"
                      @click="removeFilterChip(chip.id)"
                    >
                      <i
                        v-if="chip.icon"
                        class="mdi shrink-0 text-[13px] text-primary/70"
                        :class="chip.icon"
                      ></i>
                      <span class="shrink-0 text-slate-400 dark:text-slate-500">
                        {{ chip.label }}:
                      </span>
                      <span class="truncate max-w-[10rem]">{{ chip.value }}</span>
                      <span
                        class="flex shrink-0 items-center justify-center w-6 h-6 rounded-md text-slate-400 transition-colors group-hover:text-primary group-hover:bg-primary/10"
                      >
                        <i class="mdi mdi-close text-[14px]"></i>
                      </span>
                    </button>
                  </div>

                  <button
                    type="button"
                    class="shrink-0 inline-flex items-center gap-1 h-8 px-2.5 text-[11px] font-semibold rounded-lg text-primary hover:bg-primary/8 transition-colors"
                    @click="clearFilters"
                  >
                    <i class="mdi mdi-filter-off-outline text-[14px]"></i>
                    {{ t("consult_filter_clear_all") }}
                  </button>
                </div>
              </div>
            </header>

            <div class="p-3.5 sm:p-5">
              <div
                v-if="isInitialLoading"
                class="flex flex-col items-center justify-center py-14 text-center"
              >
                <i class="mb-3 text-3xl mdi mdi-loading mdi-spin text-primary/70"></i>
                <p class="text-sm text-slate-500">{{ t("loading") }}</p>
              </div>

              <div
                v-else-if="!consultations.length"
                class="flex flex-col items-center justify-center px-4 py-10 text-center border border-dashed rounded-2xl border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40"
              >
                <i
                  class="mb-3 text-3xl mdi mdi-forum-outline text-secondary"
                ></i>
                <p class="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {{
                    hasActiveFilters
                      ? t("consult_my_empty_filtered")
                      : t("consult_my_empty_title")
                  }}
                </p>
                <p
                  v-if="!hasActiveFilters"
                  class="mt-1 max-w-sm text-xs text-slate-500 dark:text-slate-400"
                >
                  {{ t("consult_all_subtitle") }}
                </p>
                <button
                  v-if="hasActiveFilters"
                  type="button"
                  class="inline-flex items-center gap-1.5 px-4 py-2 mt-4 text-xs font-semibold rounded-lg text-primary bg-primary/10 hover:bg-primary/15 transition-colors"
                  @click="clearFilters"
                >
                  <i class="mdi mdi-filter-off-outline text-[16px]"></i>
                  {{ t("consult_filter_clear_all") }}
                </button>
              </div>

              <!-- Consultations Data View (Table on Desktop / Cards on Mobile) -->
              <div
                v-else
                class="rounded-2xl overflow-hidden"
                :class="isRefreshing ? 'opacity-60 pointer-events-none' : ''"
              >
                <!-- 📱 Mobile Card View -->
                <div class="block md:hidden space-y-3">
                  <div
                    v-for="consult in consultations"
                    :key="consult.id"
                    class="p-4 rounded-xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm hover:border-primary/30 transition flex flex-col gap-3"
                  >
                    <!-- Header: Number badge & Date -->
                    <div class="flex items-center justify-between gap-2">
                      <ConsultationNumberBadge
                        v-if="consult.consultation_number || consult.number"
                        :consultation-number="consult.consultation_number || consult.number"
                        :copy-title="t('consult_copy_number')"
                      />
                      <span v-else class="text-xs text-slate-400">—</span>

                      <span
                        v-if="consult.date"
                        class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-200"
                      >
                        <i class="mdi mdi-calendar-month-outline text-[13px]"></i>
                        {{ consult.date }}
                      </span>
                    </div>

                    <!-- Subject & Description -->
                    <div class="flex flex-col gap-1">
                      <h3
                        class="text-sm font-semibold text-slate-900 dark:text-white leading-snug line-clamp-2"
                        :title="consult.subject || ''"
                      >
                        {{ consult.subject || "—" }}
                      </h3>
                      <p
                        class="text-xs text-slate-500 dark:text-slate-400 line-clamp-2"
                        :title="consult.description || ''"
                      >
                        {{
                          consult.description
                            ? consult.description
                            : t("consult_table_no_desc")
                        }}
                      </p>
                    </div>

                    <!-- Meta: Type -->
                    <div class="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-[11px]">
                      <span
                        v-if="consult.type"
                        class="inline-flex items-center gap-1 px-2 py-0.5 font-medium rounded-lg border bg-white dark:bg-slate-900"
                        :class="typeBadgeClass(consult.type)"
                      >
                        <i class="mdi mdi-shape-outline text-[13px] shrink-0"></i>
                        <span>{{ t(typeLabelKey(consult.type)) }}</span>
                      </span>
                      <span v-else class="text-slate-400">—</span>
                    </div>

                    <!-- Actions Bottom Bar -->
                    <div class="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                      <button
                        type="button"
                        class="flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-semibold rounded-lg bg-primary/10 hover:bg-primary/15 text-primary transition"
                        @click="handleView(consult)"
                      >
                        <i class="mdi mdi-eye-outline text-[16px]"></i>
                        <span>{{ t("consult_action_view") }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- 🖥️ Desktop / Tablet Table View -->
                <div class="hidden md:block border rounded-2xl border-slate-100 dark:border-slate-800 overflow-hidden">
                  <div class="overflow-x-auto custom-scrollbar">
                    <table class="w-full min-w-[780px] text-sm table-fixed">
                      <caption class="sr-only">
                        {{ t("consult_all_title") }}
                      </caption>

                      <colgroup>
                        <col class="w-[18%]" />
                        <col class="w-[32%]" />
                        <col class="w-[20%]" />
                        <col class="w-[20%]" />
                        <col class="w-[10%]" />
                      </colgroup>

                      <thead
                        class="sticky top-0 z-10 text-xs font-medium tracking-wide uppercase text-slate-600 bg-secondary/10 dark:bg-slate-800/90 dark:text-slate-200"
                      >
                        <tr>
                          <th
                            scope="col"
                            class="px-3.5 py-3 whitespace-nowrap"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("consult_table_number") }}
                          </th>
                          <th
                            scope="col"
                            class="px-3.5 py-3"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("consult_table_subject") }}
                          </th>
                          <th
                            scope="col"
                            class="px-3.5 py-3 whitespace-nowrap"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("consult_table_type") }}
                          </th>
                          <th
                            scope="col"
                            class="px-3.5 py-3 whitespace-nowrap"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("consult_table_created_at") }}
                          </th>
                          <th
                            scope="col"
                            class="px-3.5 py-3 text-center whitespace-nowrap"
                          >
                            {{ t("consult_table_actions") }}
                          </th>
                        </tr>
                      </thead>

                      <tbody
                        class="divide-y divide-slate-100 dark:divide-slate-800"
                      >
                        <tr
                          v-for="consult in consultations"
                          :key="consult.id"
                          class="transition-colors hover:bg-primary/5 dark:hover:bg-slate-800/60 even:bg-slate-50/60 dark:even:bg-slate-900/40"
                        >
                          <!-- Number -->
                          <td class="px-3.5 py-3 align-middle whitespace-nowrap">
                            <ConsultationNumberBadge
                              v-if="consult.consultation_number || consult.number"
                              :consultation-number="consult.consultation_number || consult.number"
                              :copy-title="t('consult_copy_number')"
                            />
                            <span v-else class="text-[11px] text-slate-400">—</span>
                          </td>

                          <!-- Subject -->
                          <td class="px-3.5 py-3 align-middle min-w-0">
                            <div class="flex flex-col gap-0.5">
                              <span
                                class="text-sm font-medium leading-5 text-slate-900 dark:text-slate-50 truncate"
                                :title="consult.subject || ''"
                              >
                                {{ consult.subject || "—" }}
                              </span>
                              <span
                                class="text-[11px] text-slate-500 dark:text-slate-400 leading-4 truncate"
                                :title="consult.description || ''"
                              >
                                {{
                                  consult.description
                                    ? consult.description
                                    : t("consult_table_no_desc")
                                }}
                              </span>
                            </div>
                          </td>

                          <!-- Type -->
                          <td class="px-3.5 py-3 align-middle min-w-0">
                            <span
                              v-if="consult.type"
                              class="inline-flex items-start gap-1 max-w-full min-w-0 px-2 py-1 text-[11px] font-medium leading-snug rounded-lg border bg-white dark:bg-slate-900"
                              :class="typeBadgeClass(consult.type)"
                            >
                              <i
                                class="mdi mdi-shape-outline text-[14px] shrink-0 mt-0.5"
                              ></i>
                              <span class="min-w-0 break-words">
                                {{ t(typeLabelKey(consult.type)) }}
                              </span>
                            </span>
                            <span
                              v-else
                              class="text-[11px] text-slate-400 dark:text-slate-500"
                            >
                              {{ t("consult_table_type_none") }}
                            </span>
                          </td>

                          <!-- Date -->
                          <td class="px-3.5 py-3 align-middle whitespace-nowrap">
                            <span
                              v-if="consult.date"
                              class="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-200"
                            >
                              <i class="mdi mdi-calendar-month-outline text-[12px]"></i>
                              {{ consult.date }}
                            </span>
                            <span v-else class="text-[11px] text-slate-400">—</span>
                          </td>

                          <!-- Actions -->
                          <td
                            class="relative px-3.5 py-3 text-center align-middle whitespace-nowrap overflow-visible"
                          >
                            <div
                              class="relative inline-flex items-center justify-center"
                              :class="openActionId === consult.id ? 'z-30' : 'z-0'"
                            >
                              <button
                                type="button"
                                class="inline-flex items-center justify-center w-8 h-8 transition bg-white border rounded-full text-slate-500 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:text-primary hover:border-primary/40 hover:bg-primary/5"
                                @click.stop="toggleActionMenu(consult.id)"
                                :aria-label="t('consult_table_actions')"
                              >
                                <i
                                  class="mdi mdi-dots-horizontal text-[18px]"
                                ></i>
                              </button>

                              <div
                                v-if="openActionId === consult.id"
                                class="absolute end-0 bottom-full mb-1 z-50 w-40 p-1 text-xs bg-white border shadow-lg rounded-xl dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-100"
                                @click.stop
                              >
                                <button
                                  type="button"
                                  class="flex items-center w-full px-3 py-2 text-left transition rounded-lg hover:bg-secondary/10 hover:text-secondary"
                                  @click="handleView(consult)"
                                >
                                  <i
                                    class="mdi mdi-eye-outline text-[16px]"
                                    :class="lang === 'ar' ? 'ml-2' : 'mr-2'"
                                  ></i>
                                  <span>{{ t("consult_action_view") }}</span>
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Pagination -->
                <div
                  class="flex flex-col gap-3 px-4 py-3 bg-white border-t dark:bg-slate-900 border-slate-100 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between mt-3 md:mt-0"
                >
                  <div class="text-xs text-slate-600 dark:text-slate-300">
                    <span v-if="pagination.total">
                      {{ lang === "ar" ? "عرض" : "Showing" }}
                      <b>{{ pagination.from }}</b
                      >–<b>{{ pagination.to }}</b>
                      {{ lang === "ar" ? "من" : "of" }}
                      <b>{{ pagination.total }}</b>
                    </span>
                    <span v-else>—</span>
                  </div>

                  <div
                    class="flex flex-wrap items-center justify-between sm:justify-end gap-3"
                  >
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-slate-500 dark:text-slate-400">
                        {{ lang === "ar" ? "عدد الصفوف" : "Rows" }}
                      </span>
                      <CompactSelect
                        v-model="perPage"
                        :options="perPageSelectOptions"
                        min-width="4.5rem"
                        align="start"
                      />
                    </div>

                    <div class="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        @click="prevPage"
                        :disabled="pagination.current <= 1 || isLoading"
                        class="inline-flex items-center justify-center text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                        aria-label="Previous Page"
                      >
                        <i
                          class="mdi mdi-chevron-left"
                          :class="lang === 'ar' ? 'rotate-180' : ''"
                        ></i>
                      </button>

                      <button
                        v-if="pagesToShow.showFirst"
                        type="button"
                        @click="goToPage(1)"
                        class="text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        1
                      </button>
                      <span
                        v-if="pagesToShow.showLeftDots"
                        class="px-1 text-slate-400"
                        >…</span
                      >

                      <button
                        v-for="p in pagesToShow.pages"
                        :key="p"
                        type="button"
                        @click="goToPage(p)"
                        class="text-xs transition border rounded-lg w-9 h-9"
                        :class="
                          p === pagination.current
                            ? 'bg-primary/10 text-primary border-primary/30'
                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                        "
                      >
                        {{ p }}
                      </button>

                      <span
                        v-if="pagesToShow.showRightDots"
                        class="px-1 text-slate-400"
                        >…</span
                      >
                      <button
                        v-if="pagesToShow.showLast"
                        type="button"
                        @click="goToPage(pagination.last)"
                        class="text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        {{ pagination.last }}
                      </button>

                      <button
                        type="button"
                        @click="nextPage"
                        :disabled="
                          pagination.current >= pagination.last || isLoading
                        "
                        class="inline-flex items-center justify-center text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                        aria-label="Next Page"
                      >
                        <i
                          class="mdi mdi-chevron-right"
                          :class="lang === 'ar' ? 'rotate-180' : ''"
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  onMounted,
  watch,
  onBeforeUnmount,
} from "vue";
import { useRouter } from "vue-router";
import { messages } from "@/i18n";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import CompactSelect from "@/components/CompactSelect.vue";
import SearchableSelect from "@/components/SearchableSelect.vue";
import FilterSearchInput from "@/components/FilterSearchInput.vue";
import ConsultationNumberBadge from "@/components/consultations/ConsultationNumberBadge.vue";
import api from "@/services/api";
import { navigateToConsultationDetails } from "@/utils/consultationDetailsBootstrap";

/* ✅ ONLY ADD: store import (consulting types) */
import { useFeedStore } from "@/stores/feedStore";

/* ---------------- Lang handling ---------------- */
const initialLang = (
  document.documentElement.lang ||
  localStorage.getItem("app_lang") ||
  "ar"
).toLowerCase();

const lang = ref(initialLang.startsWith("ar") ? "ar" : "en");
const _langObserver = ref(null);
const _langHandler = (e) => {
  const next = (e.detail || "ar").toLowerCase().startsWith("en") ? "en" : "ar";
  if (next !== lang.value) lang.value = next;
};
window.addEventListener("app:lang", _langHandler, { passive: true });

_langObserver.value = new MutationObserver(() => {
  const htmlLang = (
    document.documentElement.lang ||
    localStorage.getItem("app_lang") ||
    "ar"
  ).toLowerCase();
  const next = htmlLang.startsWith("en") ? "en" : "ar";
  if (next !== lang.value) lang.value = next;
});
_langObserver.value.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ["lang", "dir"],
});

/* ---------------- Layout state ---------------- */
const isSidebarCollapsed = ref(false);
const isSidebarOpenMobile = ref(false);
const sidebarSearch = ref("");
const router = useRouter();

function toggleSidebar() {
  if (window.innerWidth < 768)
    isSidebarOpenMobile.value = !isSidebarOpenMobile.value;
  else isSidebarCollapsed.value = !isSidebarCollapsed.value;
}

/* ---------------- i18n helper ---------------- */
function t(key) {
  return (messages[lang.value] && messages[lang.value][key]) || key;
}

function truncateText(value, limit) {
  const text = value == null ? "" : String(value);
  if (!text) return "";
  if (text.length <= limit) return text;
  return text.slice(0, limit).trim() + "...";
}

/* ---------------- API ---------------- */
const API_LIST = "/consultations";

/* ---------------- Data ---------------- */
const consultationsData = ref(null);
const consultations = computed(() => consultationsData.value?.data || []);
const totalConsultations = computed(
  () => consultationsData.value?.meta?.total || 0
);

const isLoading = ref(false);
const hasLoadedOnce = ref(false);

const searchInput = ref("");
const debouncedSearch = ref("");
let searchTimer = null;

const isInitialLoading = computed(() => isLoading.value && !hasLoadedOnce.value);
const isRefreshing = computed(() => isLoading.value && hasLoadedOnce.value);

watch(searchInput, (val) => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    debouncedSearch.value = val.trim();
    page.value = 1;
    fetchConsultations();
  }, 300);
});

const hasActiveFilters = computed(
  () => !!debouncedSearch.value || !!filters.type,
);

const activeFilterChips = computed(() => {
  const chips = [];

  if (debouncedSearch.value) {
    chips.push({
      id: "search",
      label: t("consult_filter_search"),
      value: debouncedSearch.value,
      icon: "mdi-magnify",
    });
  }

  if (filters.type) {
    const type = typeSelectOptions.value.find(
      (o) => String(o.value) === String(filters.type),
    );
    chips.push({
      id: "type",
      label: t("consult_filter_type"),
      value: type?.label || String(filters.type),
      icon: "mdi-shape-outline",
    });
  }

  return chips;
});

function clearSearch() {
  searchInput.value = "";
  debouncedSearch.value = "";
  page.value = 1;
  fetchConsultations();
}

function clearFilters() {
  searchInput.value = "";
  debouncedSearch.value = "";
  filters.type = "";
  page.value = 1;
  fetchConsultations();
}

function removeFilterChip(id) {
  if (id === "search") {
    clearSearch();
    return;
  }
  if (id === "type") {
    filters.type = "";
  }
  page.value = 1;
  fetchConsultations();
}

/* filters */
const filters = reactive({
  type: "",
  activity: "",
  owner: "",
});

/* pagination */
const page = ref(1);
const perPage = ref(10);

/* ✅ consulting types from store */
const feedStore = useFeedStore();

const typeOptions = computed(() => {
  const list =
    feedStore.consultationTypes ||
    feedStore.consultations_types ||
    feedStore.consultation_types ||
    [];

  return (Array.isArray(list) ? list : []).map((tp) => {
    const label =
      lang.value === "ar"
        ? tp?.name?.ar || tp?.title?.ar || tp?.name_ar || tp?.title_ar
        : tp?.name?.en || tp?.title?.en || tp?.name_en || tp?.title_en;

    return {
      value: Number(tp.id),
      // keep template usage: {{ t(tp.label) }} => if not a key, it prints same
      label: label || tp?.key || tp?.slug || String(tp.id),
      key: tp?.key || tp?.slug || "",
    };
  });
});

const typeSelectOptions = computed(() => [
  { value: "", label: t("consult_filter_type_all") },
  ...typeOptions.value.map((tp) => ({
    value: tp.value,
    label: t(tp.label),
  })),
]);

const perPageSelectOptions = [
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 30, label: "30" },
  { value: 50, label: "50" },
];

function findTypeFromStore(typeId) {
  const id = Number(typeId);
  const list =
    feedStore.consultationTypes ||
    feedStore.consultations_types ||
    feedStore.consultation_types ||
    [];
  return (Array.isArray(list) ? list : []).find((x) => Number(x.id) === id);
}

function typeKeyFromStore(typeId) {
  const tp = findTypeFromStore(typeId);
  const raw = tp?.key || tp?.slug || tp?.name_en || tp?.title_en || "";
  return String(raw || "")
    .toLowerCase()
    .trim();
}

const pagination = computed(() => {
  const meta = consultationsData.value?.meta || {};

  const total = Number(meta.total ?? meta.total_items ?? 0);
  const current = Number(meta.current_page ?? meta.page ?? page.value ?? 1);
  const per = Number(meta.per_page ?? meta.perPage ?? perPage.value ?? 10);

  const last = Number(meta.last_page ?? Math.max(1, Math.ceil(total / per)));
  const from = Number(meta.from ?? (total ? (current - 1) * per + 1 : 0));
  const to = Number(meta.to ?? Math.min(current * per, total));

  return { total, current, per, last, from, to };
});

const pagesToShow = computed(() => {
  const { current, last } = pagination.value;
  const delta = 2;
  const start = Math.max(1, current - delta);
  const end = Math.min(last, current + delta);

  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return {
    showFirst: start > 1,
    showLeftDots: start > 2,
    pages,
    showRightDots: end < last - 1,
    showLast: end < last,
  };
});

function goToPage(p) {
  const n = Number(p);
  if (!n) return;
  if (n < 1 || n > pagination.value.last) return;
  page.value = n;
}

function prevPage() {
  if (pagination.value.current > 1) goToPage(pagination.value.current - 1);
}

function nextPage() {
  if (pagination.value.current < pagination.value.last)
    goToPage(pagination.value.current + 1);
}

/* server fetch */
async function fetchConsultations() {
  try {
    isLoading.value = true;

    const { data } = await api.get(API_LIST, {
      params: {
        search: debouncedSearch.value || undefined,
        type_id: filters.type ? Number(filters.type) : undefined,
        activity: filters.activity || undefined,
        owner: filters.owner || undefined,
        itemsPerPage: perPage.value,
        page: page.value,
      },
      headers: { Accept: "application/json" },
    });

    consultationsData.value = {
      ...(data || {}),
      data: (data.data || []).map((c) => {
        const typeRaw =
          c.type_id ??
          c.type?.id ??
          c.type ??
          c.type_key ??
          c.type?.key ??
          c.type?.slug;

        const typeId =
          typeof typeRaw === "number" ||
          (typeof typeRaw === "string" && /^[0-9]+$/.test(typeRaw))
            ? Number(typeRaw)
            : Number(c.type?.id ?? c.type_id ?? 0) || null;

        return {
          ...c,
          number: c.consultation_number || c.number,
          consultation_number: c.consultation_number || c.number,
          subject: c.subject || "—",
          description: c.description || "—",
          date: c.date || c.created_at || "—",
          type: typeId,
        };
      }),
    };
  } catch (e) {
    console.error(e);
    consultationsData.value = { data: [], meta: { total: 0 } };
  } finally {
    isLoading.value = false;
    hasLoadedOnce.value = true;
  }
}

/* ---------------- Label keys ---------------- */
function activityLabelKey(activity) {
  if (activity === "1") return "Construction";
  return "consult_activity_unknown";
}

function typeLabelKey(typeId) {
  const tp = findTypeFromStore(typeId);
  if (!tp) return "consult_table_type_none";

  const label =
    lang.value === "ar"
      ? tp?.name?.ar || tp?.title?.ar || tp?.name_ar || tp?.title_ar
      : tp?.name?.en || tp?.title?.en || tp?.name_en || tp?.title_en;

  // returned value might be plain text; template wraps it with t() safely
  return label || tp?.key || tp?.slug || String(tp.id);
}

function statusLabelKey(status) {
  if (status === "open") return "consult_status_open";
  if (status === "in_progress") return "consult_status_in_progress";
  if (status === "answered") return "consult_status_answered";
  if (status === "closed") return "consult_status_closed";
  return "consult_status_open";
}

/* ---------------- Badges ---------------- */
function typeBadgeClass(typeId) {
  const key = typeKeyFromStore(typeId);

  if (key.includes("government")) {
    return "border-rose-300 text-rose-700 dark:border-rose-700 dark:text-rose-300 bg-rose-50/60 dark:bg-rose-950/40";
  }
  if (key.includes("marketing")) {
    return "border-secondary/50 text-secondary bg-secondary/5 dark:bg-secondary/20";
  }
  return "border-primary/40 text-primary bg-primary/5";
}

/* ---------------- Actions menu ---------------- */
const openActionId = ref(null);

function toggleActionMenu(id) {
  openActionId.value = openActionId.value === id ? null : id;
}

function handleClickOutside() {
  openActionId.value = null;
}

function handleView(consult) {
  navigateToConsultationDetails(router, consult);
}

/* ---------------- Watchers & lifecycle ---------------- */
watch(
  () => filters.type,
  () => {
    page.value = 1;
    fetchConsultations();
  },
);

watch([page, perPage], () => fetchConsultations());

watch(perPage, () => {
  page.value = 1;
});

onMounted(async () => {
  document.addEventListener("click", handleClickOutside);

  // ✅ ensure types are loaded before list (so filter + labels work)
  try {
    if (typeof feedStore.fetchFeedOnce === "function") {
      await feedStore.fetchFeedOnce({ lang: lang.value });
    }
  } catch (e) {
    // ignore store error, page still works
  }

  fetchConsultations();
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  if (searchTimer) clearTimeout(searchTimer);
  if (_langObserver.value) _langObserver.value.disconnect();
  window.removeEventListener("app:lang", _langHandler);
});
</script>
